import { LandscapeStreamline as LandscapeStreamlineIcon } from '@/components/chariot-ui';
import { Button } from '@/components/chariot-ui';
import { useState } from 'react';

import { Dropzone, Files } from '@/components/Dropzone';
import { Modal } from '@/components/Modal';

export const profilePictureImageAccept = {
  'image/*': ['.png', '.jpg', '.jpeg'],
};

type ProfilePictureSource =
  | { type: 'url'; url: string }
  | { type: 'buffer'; buffer: ArrayBuffer }
  | { type: 'empty' };

interface ProfilePictureUploadProps {
  source: ProfilePictureSource;
  onUpload: (file: ArrayBuffer) => void;
  onDelete: () => void;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  shape?: 'circle' | 'square';
  disabled?: boolean;
  ariaLabel?: string;
  modalTitle?: string;
  emptyStateText?: string;
  className?: string;
  'data-testid'?: string;
}

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  source,
  onUpload,
  onDelete,
  size = 'md',
  shape = 'circle',
  disabled = false,
  ariaLabel,
  modalTitle,
  emptyStateText,
  className = '',
  'data-testid': dataTestId,
}) => {
  const [showImageModal, setShowImageModal] = useState(false);

  const handleFileDrop = (files: Files<'arrayBuffer'>): void => {
    if (files.length === 1) {
      const content = files[0].content;
      onUpload(content);
      setShowImageModal(false);
    }
  };

  const sizeClasses = {
    xs: 'size-12',
    sm: 'size-16',
    md: 'size-24',
    lg: 'size-32',
  };

  const modalPreviewSizeClasses = {
    xs: 'size-20',
    sm: 'size-24',
    md: 'size-32',
    lg: 'size-40',
  };

  const shapeClasses = {
    circle: 'rounded-full',
    square: 'rounded-lg',
  };

  const getImageUrl = (): string | null => {
    if (source.type === 'url') {
      return source.url;
    }
    if (source.type === 'buffer') {
      return URL.createObjectURL(new Blob([source.buffer]));
    }
    return null;
  };

  const imageUrl = getImageUrl();
  const hasImage = source.type !== 'empty';

  return (
    <>
      {hasImage && imageUrl ? (
        <div
          className={`bg-chariot-background-secondary flex shrink-0 items-center justify-center overflow-hidden transition-opacity ${sizeClasses[size]} ${shapeClasses[shape]} ${
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'focus:ring-chariot-brand-primary cursor-pointer hover:opacity-80 focus:ring-2 focus:ring-offset-2 focus:outline-none'
          } ${className}`}
          onClick={() => !disabled && setShowImageModal(true)}
          onKeyDown={e => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setShowImageModal(true);
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel || 'Change image'}
          data-testid={dataTestId}
        >
          <img
            src={imageUrl}
            alt="Profile"
            className="size-full object-cover"
          />
        </div>
      ) : (
        <Dropzone
          type="arrayBuffer"
          onFilesDrop={disabled ? () => {} : handleFileDrop}
          title={emptyStateText || 'Click or drag and drop your image here.'}
          subTitle=""
          accept={profilePictureImageAccept}
          className={`border-chariot-stroke flex shrink-0 flex-col items-center justify-center border-[2px] border-dashed p-0 ${sizeClasses[size]} ${shapeClasses[shape]} ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-chariot-brand-primary cursor-pointer'} ${className}`}
          data-testid={dataTestId}
        >
          <LandscapeStreamlineIcon className="text-chariot-stroke size-8" />
        </Dropzone>
      )}

      {showImageModal && hasImage && imageUrl && (
        <Modal
          open={showImageModal}
          onClose={() => setShowImageModal(false)}
          title={modalTitle || 'Change Image'}
          size="lg"
          footer={{
            hideCancelButton: true,
          }}
        >
          <div className="flex flex-col items-center space-y-6">
            <div
              className={`bg-chariot-background-secondary overflow-hidden ${modalPreviewSizeClasses[size]} ${shapeClasses[shape]}`}
            >
              <img
                src={imageUrl}
                alt="Profile Preview"
                className="size-full object-cover"
              />
            </div>

            <Dropzone
              type="arrayBuffer"
              onFilesDrop={handleFileDrop}
              title=""
              subTitle=""
              accept={profilePictureImageAccept}
              className="border-chariot-stroke hover:border-chariot-brand-primary w-full rounded-lg border-2 border-dashed p-8"
              data-testid={dataTestId ? `${dataTestId}-modal` : undefined}
            >
              <div className="flex flex-col items-center gap-2">
                <svg
                  className="text-chariot-text-secondary size-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-chariot-text-primary text-sm font-medium">
                  Click or drag to upload new image
                </p>
                <p className="text-chariot-text-secondary text-xs">
                  PNG, JPG or JPEG
                </p>
              </div>
            </Dropzone>

            {/* Visual separator with OR text */}
            <div className="my-6 flex items-center">
              <div className="border-chariot-stroke flex-1 border-t"></div>
              <span className="text-chariot-text-secondary px-4 text-xs tracking-wider uppercase">
                or
              </span>
              <div className="border-chariot-stroke flex-1 border-t"></div>
            </div>

            <Button
              label="Delete Image"
              styleType="tertiary"
              size="sm"
              onClick={() => {
                onDelete();
                setShowImageModal(false);
              }}
              className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 text-white"
              data-testid={
                dataTestId ? `${dataTestId}-delete-button` : undefined
              }
            />
          </div>
        </Modal>
      )}
    </>
  );
};
